// app_client/src/pages/admin/SystemSettings.jsx
import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'react-hot-toast';
import {
  AlertCircle,
  Bell,
  Building2,
  ChevronLeft,
  ChevronRight,
  Database,
  Loader2,
  Mail,
  MapPin,
  Save,
  Send,
  ShieldCheck,
  SlidersHorizontal,
  Timer
} from 'lucide-react';
import LocationSettings from '../../components/systemsettings/LocationSettings';

const defaultForm = {
  serverIp: '',
  companyDetails: {
    name: '',
    title: '',
    taxOffice: '',
    taxNumber: '',
    address: '',
    phone: '',
    email: ''
  },
  smtpSettings: {
    host: '',
    port: 587,
    user: '',
    pass: '',
    secure: false,
    fromEmail: ''
  },
  systemLimits: {
    maxFileSizeMb: 10,
    sessionTimeoutMin: 60
  }
};

const mergeSystemConfig = (config = {}) => ({
  serverIp: config.serverIp || '',
  companyDetails: {
    ...defaultForm.companyDetails,
    ...(config.companyDetails || {})
  },
  smtpSettings: {
    ...defaultForm.smtpSettings,
    ...(config.smtpSettings || {})
  },
  systemLimits: {
    ...defaultForm.systemLimits,
    ...(config.systemLimits || {})
  }
});

const Field = ({ label, value, onChange, type = 'text', placeholder, multiline = false }) => (
  <label className="block">
    <span className="text-[10px] font-black uppercase tracking-widest text-slate-400">{label}</span>
    {multiline ? (
      <textarea
        value={value}
        onChange={(event) => onChange(event.target.value)}
        placeholder={placeholder}
        rows={4}
        className="mt-1 w-full resize-none rounded-2xl border border-slate-200 bg-white px-4 py-3 text-sm font-bold text-slate-700 outline-none transition focus:border-blue-500 focus:ring-4 focus:ring-blue-100"
      />
    ) : (
      <input
        type={type}
        value={value}
        onChange={(event) => onChange(event.target.value)}
        placeholder={placeholder}
        className="mt-1 w-full rounded-2xl border border-slate-200 bg-white px-4 py-3 text-sm font-bold text-slate-700 outline-none transition focus:border-blue-500 focus:ring-4 focus:ring-blue-100"
      />
    )}
  </label>
);

const FormSection = ({ icon, title, description, children }) => (
  <section className="rounded-3xl border border-slate-200 bg-white p-6 shadow-sm">
    <div className="mb-6 flex items-start gap-4">
      <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-2xl bg-blue-50 text-blue-600">
        {icon}
      </div>
      <div>
        <h2 className="text-lg font-black text-slate-900">{title}</h2>
        <p className="mt-1 text-sm font-medium leading-6 text-slate-500">{description}</p>
      </div>
    </div>
    {children}
  </section>
);

const moduleOptions = [
  { value: '', label: 'Tümü' },
  { value: 'system', label: 'Sistem Ayarları' },
  { value: 'operation', label: 'Operasyonlar' },
  { value: 'invoice', label: 'Faturalar' },
  { value: 'account', label: 'Hesaplar' },
  { value: 'auth', label: 'Kimlik Doğrulama' }
];

const actionOptions = [
  { value: '', label: 'Tümü' },
  { value: 'UPDATE', label: 'UPDATE' },
  { value: 'STATUS_CHANGE', label: 'STATUS_CHANGE' },
  { value: 'CREATE', label: 'CREATE' },
  { value: 'DELETE', label: 'DELETE' },
  { value: 'LOGIN_SUCCESS', label: 'LOGIN_SUCCESS' }
];

const actionTone = {
  UPDATE: 'bg-amber-50 text-amber-700 ring-amber-100',
  STATUS_CHANGE: 'bg-blue-50 text-blue-700 ring-blue-100',
  CREATE: 'bg-emerald-50 text-emerald-700 ring-emerald-100',
  DELETE: 'bg-rose-50 text-rose-700 ring-rose-100',
  LOGIN_SUCCESS: 'bg-violet-50 text-violet-700 ring-violet-100'
};

const moduleTone = {
  system: 'bg-slate-900 text-white ring-slate-900',
  operation: 'bg-blue-50 text-blue-700 ring-blue-100',
  invoice: 'bg-cyan-50 text-cyan-700 ring-cyan-100',
  account: 'bg-emerald-50 text-emerald-700 ring-emerald-100',
  auth: 'bg-violet-50 text-violet-700 ring-violet-100'
};

const formatLogDate = (value) => {
  if (!value) return '-';

  return new Intl.DateTimeFormat('tr-TR', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit'
  }).format(new Date(value));
};

const SelectField = ({ label, value, options, onChange }) => (
  <label className="block">
    <span className="text-[10px] font-black uppercase tracking-widest text-slate-400">{label}</span>
    <select
      value={value}
      onChange={(event) => onChange(event.target.value)}
      className="mt-1 w-full rounded-2xl border border-slate-200 bg-white px-4 py-3 text-sm font-black text-slate-700 outline-none transition focus:border-blue-500 focus:ring-4 focus:ring-blue-100"
    >
      {options.map((option) => (
        <option key={option.value || 'all'} value={option.value}>
          {option.label}
        </option>
      ))}
    </select>
  </label>
);

const SystemSettings = () => {
  const [activeTab, setActiveTab] = useState('general');
  const [form, setForm] = useState(defaultForm);
  const [loadingConfig, setLoadingConfig] = useState(false);
  const [savingConfig, setSavingConfig] = useState(false);
  const [auditLogs, setAuditLogs] = useState([]);
  const [auditPagination, setAuditPagination] = useState({ page: 1, limit: 20, total: 0, pages: 1 });
  const [auditPage, setAuditPage] = useState(1);
  const [auditModule, setAuditModule] = useState('');
  const [auditAction, setAuditAction] = useState('');
  const [loadingAuditLogs, setLoadingAuditLogs] = useState(false);
  const [auditError, setAuditError] = useState('');
  const [notificationRules, setNotificationRules] = useState([]);
  const [loadingNotificationRules, setLoadingNotificationRules] = useState(false);
  const [savingNotificationRules, setSavingNotificationRules] = useState(false);
  const [testingSmtp, setTestingSmtp] = useState(false);

const tabs = [
    { id: 'general', name: 'Genel Ayarlar', icon: <SlidersHorizontal size={18} /> },
    { id: 'locations', name: 'Bölge Tanımları', icon: <MapPin size={18} /> },
    { id: 'security', name: 'Güvenlik & Log', icon: <ShieldCheck size={18} /> },
    { id: 'notifications', name: 'Bildirim Ayarları', icon: <Bell size={18} /> },
    { id: 'backup', name: 'Yedekleme', icon: <Database size={18} /> },
];

const normalizeRoleName = (value) => String(value || '')
  .trim()
  .toLowerCase()
  .normalize('NFD')
  .replace(/[\u0300-\u036f]/g, '')
  .replace(/ı/g, 'i')
  .replace(/[^a-z0-9]/g, '');

  const placeholderContent = {
    backup: {
      title: 'Yedekleme',
      description: 'Yedekleme periyotları, arşivleme ve geri yükleme işlemleri bu merkeze bağlanacak.',
      icon: <Database size={28} />
    }
  };

  useEffect(() => {
    const controller = new AbortController();

    const fetchSystemConfig = async () => {
      setLoadingConfig(true);

      try {
        const { data } = await axios.get('/api/system', {
          signal: controller.signal
        });
        setForm(mergeSystemConfig(data));
      } catch (error) {
        if (error.name !== 'CanceledError') {
          toast.error(error.response?.data?.message || 'Sistem ayarları yüklenemedi');
        }
      } finally {
        if (!controller.signal.aborted) {
          setLoadingConfig(false);
        }
      }
    };

    fetchSystemConfig();

    return () => controller.abort();
  }, []);

  useEffect(() => {
    if (activeTab !== 'security') return undefined;

    const controller = new AbortController();

    const fetchAuditLogs = async () => {
      setLoadingAuditLogs(true);
      setAuditError('');

      try {
        const { data } = await axios.get('/api/system/logs', {
          signal: controller.signal,
          params: {
            page: auditPage,
            limit: auditPagination.limit,
            module: auditModule || undefined,
            action: auditAction || undefined
          }
        });

        setAuditLogs(Array.isArray(data.data) ? data.data : []);
        setAuditPagination(data.pagination || { page: auditPage, limit: auditPagination.limit, total: 0, pages: 1 });
      } catch (error) {
        if (error.name !== 'CanceledError') {
          const message = error.response?.data?.message || 'Audit log kayıtları yüklenemedi';
          setAuditError(message);
          toast.error(message);
        }
      } finally {
        if (!controller.signal.aborted) {
          setLoadingAuditLogs(false);
        }
      }
    };

    fetchAuditLogs();

    return () => controller.abort();
  }, [activeTab, auditAction, auditModule, auditPage, auditPagination.limit]);

  useEffect(() => {
    if (activeTab !== 'notifications') return undefined;

    const controller = new AbortController();

    const fetchNotificationRules = async () => {
      setLoadingNotificationRules(true);

      try {
        const { data } = await axios.get('/api/system/notification-rules', {
          signal: controller.signal
        });
        setNotificationRules(Array.isArray(data) ? data : []);
      } catch (error) {
        if (error.name !== 'CanceledError') {
          toast.error(error.response?.data?.message || 'Bildirim kuralları yüklenemedi');
        }
      } finally {
        if (!controller.signal.aborted) {
          setLoadingNotificationRules(false);
        }
      }
    };

    fetchNotificationRules();

    return () => controller.abort();
  }, [activeTab]);

  const updateRootField = (field, value) => {
    setForm((current) => ({
      ...current,
      [field]: value
    }));
  };

  const updateNestedField = (section, field, value) => {
    setForm((current) => ({
      ...current,
      [section]: {
        ...current[section],
        [field]: value
      }
    }));
  };

  const handleSave = async () => {
    setSavingConfig(true);

    const payload = {
      ...form,
      smtpSettings: {
        ...form.smtpSettings,
        port: Number(form.smtpSettings.port) || 0
      },
      systemLimits: {
        maxFileSizeMb: Number(form.systemLimits.maxFileSizeMb) || 10,
        sessionTimeoutMin: Number(form.systemLimits.sessionTimeoutMin) || 60
      }
    };

    try {
      const { data } = await axios.put('/api/system', payload);
      setForm(mergeSystemConfig(data));
      toast.success('Sistem ayarları güncellendi');
    } catch (error) {
      toast.error(error.response?.data?.message || 'Sistem ayarları güncellenemedi');
    } finally {
      setSavingConfig(false);
    }
  };

  const handleTestSmtp = async () => {
    setTestingSmtp(true);

    try {
      const { data } = await axios.put('/api/system/test-smtp', {
        smtpSettings: {
          ...form.smtpSettings,
          port: Number(form.smtpSettings.port) || 0
        }
      });
      toast.success(data.message || 'SMTP bağlantısı başarılı');
    } catch (error) {
      toast.error(error.response?.data?.error || error.response?.data?.message || 'SMTP bağlantısı başarısız');
    } finally {
      setTestingSmtp(false);
    }
  };

  const handleAuditModuleChange = (value) => {
    setAuditModule(value);
    setAuditPage(1);
  };

  const handleAuditActionChange = (value) => {
    setAuditAction(value);
    setAuditPage(1);
  };

  const goToAuditPage = (page) => {
    const lastPage = Math.max(auditPagination.pages || 1, 1);
    setAuditPage(Math.min(Math.max(page, 1), lastPage));
  };

  const updateNotificationRule = (eventKey, field, value) => {
    setNotificationRules((currentRules) => (
      currentRules.map((rule) => (
        rule.eventKey === eventKey ? { ...rule, [field]: value } : rule
      ))
    ));
  };

  const handleSaveNotificationRules = async () => {
    setSavingNotificationRules(true);

    try {
      const { data } = await axios.put('/api/system/notification-rules', notificationRules);
      setNotificationRules(Array.isArray(data) ? data : []);
      toast.success('Bildirim ayarları güncellendi');
    } catch (error) {
      toast.error(error.response?.data?.message || 'Bildirim ayarları güncellenemedi');
    } finally {
      setSavingNotificationRules(false);
    }
  };

  const activePlaceholder = placeholderContent[activeTab];

  return (
    <div className="space-y-6 pb-20">
      <header className="flex flex-col gap-4 border-b border-slate-200 pb-5 lg:flex-row lg:items-end lg:justify-between">
        <div>
          <p className="text-xs font-black uppercase tracking-widest text-blue-600">Sistem Merkezi</p>
          <h1 className="mt-1 text-3xl font-black tracking-tight text-slate-900">Genel Ayarlar</h1>
          <p className="mt-2 max-w-2xl text-sm font-medium text-slate-500">
            Şirket bilgileri, SMTP ve sistem limitlerini merkezi konfigürasyondan yönetin.
          </p>
        </div>

        <nav className="flex flex-wrap gap-2 rounded-2xl bg-white p-2 shadow-sm ring-1 ring-slate-200">
          {tabs.map((tab) => (
            <button
              type="button"
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`flex items-center gap-2 rounded-xl px-4 py-2.5 text-sm font-bold transition-all ${
                activeTab === tab.id
                  ? 'bg-blue-600 text-white shadow-lg shadow-blue-200'
                  : 'text-slate-500 hover:bg-slate-100 hover:text-slate-900'
              }`}
            >
              {tab.icon}
              {tab.name}
            </button>
          ))}
        </nav>
      </header>

      <main>
        {activeTab === 'general' && (
          <div className="space-y-6">
            {loadingConfig ? (
              <div className="flex min-h-80 items-center justify-center rounded-3xl border border-slate-200 bg-white text-slate-400 shadow-sm">
                <div className="flex flex-col items-center gap-3">
                  <Loader2 size={34} className="animate-spin text-blue-600" />
                  <p className="text-xs font-black uppercase tracking-widest">Sistem ayarları yükleniyor</p>
                </div>
              </div>
            ) : (
              <>
                <div className="grid gap-6 xl:grid-cols-2">
                  <FormSection
                    icon={<Building2 size={24} />}
                    title="Şirket Bilgileri"
                    description="Fatura, yazışma ve kurumsal çıktılarda kullanılacak temel şirket profili."
                  >
                    <div className="grid gap-4 sm:grid-cols-2">
                      <Field label="Şirket Adı" value={form.companyDetails.name} onChange={(value) => updateNestedField('companyDetails', 'name', value)} />
                      <Field label="Unvan" value={form.companyDetails.title} onChange={(value) => updateNestedField('companyDetails', 'title', value)} />
                      <Field label="Vergi Dairesi" value={form.companyDetails.taxOffice} onChange={(value) => updateNestedField('companyDetails', 'taxOffice', value)} />
                      <Field label="Vergi No" value={form.companyDetails.taxNumber} onChange={(value) => updateNestedField('companyDetails', 'taxNumber', value)} />
                      <Field label="Telefon" value={form.companyDetails.phone} onChange={(value) => updateNestedField('companyDetails', 'phone', value)} />
                      <Field label="E-posta" type="email" value={form.companyDetails.email} onChange={(value) => updateNestedField('companyDetails', 'email', value)} />
                      <div className="sm:col-span-2">
                        <Field label="Adres" value={form.companyDetails.address} onChange={(value) => updateNestedField('companyDetails', 'address', value)} multiline />
                      </div>
                    </div>
                  </FormSection>

                  <FormSection
                    icon={<Mail size={24} />}
                    title="SMTP Ayarları"
                    description="Kurumsal e-posta gönderimleri için kullanılacak SMTP sunucu bilgileri."
                  >
                    <div className="grid gap-4 sm:grid-cols-2">
                      <Field label="Sunucu Host" value={form.smtpSettings.host} onChange={(value) => updateNestedField('smtpSettings', 'host', value)} />
                      <Field label="Port" type="number" value={form.smtpSettings.port} onChange={(value) => updateNestedField('smtpSettings', 'port', value)} />
                      <Field label="Kullanıcı Adı" value={form.smtpSettings.user} onChange={(value) => updateNestedField('smtpSettings', 'user', value)} />
                      <Field label="Şifre" type="password" value={form.smtpSettings.pass} onChange={(value) => updateNestedField('smtpSettings', 'pass', value)} />
                      <Field label="Gönderici E-postası" type="email" value={form.smtpSettings.fromEmail} onChange={(value) => updateNestedField('smtpSettings', 'fromEmail', value)} />
                      <label className="flex items-center justify-between rounded-2xl border border-slate-200 px-4 py-3">
                        <span>
                          <span className="block text-sm font-black text-slate-700">SSL/TLS</span>
                          <span className="block text-xs font-medium text-slate-400">Güvenli SMTP bağlantısı kullan.</span>
                        </span>
                        <input
                          type="checkbox"
                          checked={Boolean(form.smtpSettings.secure)}
                          onChange={(event) => updateNestedField('smtpSettings', 'secure', event.target.checked)}
                          className="h-5 w-5 rounded border-slate-300 text-blue-600 focus:ring-blue-500"
                        />
                      </label>
                    </div>
                    <div className="mt-5 flex justify-end border-t border-slate-100 pt-5">
                      <button
                        type="button"
                        onClick={handleTestSmtp}
                        disabled={testingSmtp}
                        className="flex items-center gap-2 rounded-2xl border border-blue-100 bg-blue-50 px-5 py-3 text-xs font-black uppercase tracking-widest text-blue-700 transition hover:bg-blue-100 disabled:cursor-not-allowed disabled:opacity-60"
                      >
                        {testingSmtp ? <Loader2 size={17} className="animate-spin" /> : <Send size={17} />}
                        SMTP Bağlantısını Test Et
                      </button>
                    </div>
                  </FormSection>
                </div>

                <FormSection
                  icon={<Timer size={24} />}
                  title="Sistem Limitleri"
                  description="Dosya yükleme ve oturum davranışları için global limitler."
                >
                  <div className="grid gap-4 md:grid-cols-3">
                    <Field label="Sunucu IP" value={form.serverIp} onChange={(value) => updateRootField('serverIp', value)} />
                    <Field label="Maksimum Dosya Boyutu (MB)" type="number" value={form.systemLimits.maxFileSizeMb} onChange={(value) => updateNestedField('systemLimits', 'maxFileSizeMb', value)} />
                    <Field label="Oturum Zaman Aşımı (Dakika)" type="number" value={form.systemLimits.sessionTimeoutMin} onChange={(value) => updateNestedField('systemLimits', 'sessionTimeoutMin', value)} />
                  </div>
                </FormSection>

                <div className="flex justify-end">
                  <button
                    type="button"
                    onClick={handleSave}
                    disabled={savingConfig}
                    className="flex items-center gap-2 rounded-2xl bg-blue-600 px-6 py-3 text-sm font-black text-white shadow-lg shadow-blue-200 transition hover:bg-blue-700 disabled:cursor-not-allowed disabled:opacity-60"
                  >
                    {savingConfig ? <Loader2 size={18} className="animate-spin" /> : <Save size={18} />}
                    Sistem Ayarlarını Güncelle
                  </button>
                </div>
              </>
            )}
          </div>
        )}

        {activeTab === 'security' && (
          <div className="space-y-6">
            <section className="rounded-3xl border border-slate-200 bg-white p-6 shadow-sm">
              <div className="mb-6 flex flex-col gap-4 lg:flex-row lg:items-end lg:justify-between">
                <div>
                  <div className="mb-4 flex h-12 w-12 items-center justify-center rounded-2xl bg-blue-50 text-blue-600">
                    <ShieldCheck size={24} />
                  </div>
                  <h2 className="text-xl font-black text-slate-900">Siber Denetim Kayıtları</h2>
                  <p className="mt-2 max-w-2xl text-sm font-medium leading-6 text-slate-500">
                    Hassas ERP operasyonları, sistem değişiklikleri ve güvenlik olaylarını gerçek zamanlı izleyin.
                  </p>
                </div>

                <div className="grid w-full gap-3 sm:grid-cols-2 lg:max-w-xl">
                  <SelectField
                    label="Modül"
                    value={auditModule}
                    options={moduleOptions}
                    onChange={handleAuditModuleChange}
                  />
                  <SelectField
                    label="Aksiyon"
                    value={auditAction}
                    options={actionOptions}
                    onChange={handleAuditActionChange}
                  />
                </div>
              </div>

              {loadingAuditLogs ? (
                <div className="space-y-3">
                  {[0, 1, 2, 3].map((item) => (
                    <div key={item} className="animate-pulse rounded-2xl border border-slate-100 bg-slate-50 p-4">
                      <div className="mb-3 h-3 w-40 rounded-full bg-slate-200" />
                      <div className="mb-2 h-4 w-3/4 rounded-full bg-slate-200" />
                      <div className="h-3 w-1/2 rounded-full bg-slate-200" />
                    </div>
                  ))}
                </div>
              ) : auditError ? (
                <div className="flex min-h-64 flex-col items-center justify-center rounded-2xl border border-rose-100 bg-rose-50 text-rose-600">
                  <AlertCircle size={34} />
                  <p className="mt-3 text-sm font-black">{auditError}</p>
                </div>
              ) : auditLogs.length === 0 ? (
                <div className="flex min-h-64 flex-col items-center justify-center rounded-2xl border border-dashed border-slate-300 bg-slate-50 text-slate-400">
                  <ShieldCheck size={38} className="opacity-30" />
                  <p className="mt-3 text-xs font-black uppercase tracking-widest">Kayıt bulunamadı</p>
                </div>
              ) : (
                <div className="overflow-hidden rounded-2xl border border-slate-200">
                  <div className="divide-y divide-slate-100">
                    {auditLogs.map((log) => (
                      <article key={log._id} className="bg-white p-5 transition-colors hover:bg-slate-50/80">
                        <div className="flex flex-col gap-4 lg:flex-row lg:items-start lg:justify-between">
                          <div className="min-w-0 flex-1">
                            <div className="mb-3 flex flex-wrap items-center gap-2">
                              <span className="rounded-full bg-slate-100 px-3 py-1 text-[10px] font-black uppercase tracking-widest text-slate-500">
                                {formatLogDate(log.createdAt)}
                              </span>
                              <span className={`rounded-full px-3 py-1 text-[10px] font-black uppercase tracking-widest ring-1 ${moduleTone[log.module] || 'bg-slate-50 text-slate-600 ring-slate-100'}`}>
                                {log.module}
                              </span>
                              <span className={`rounded-full px-3 py-1 text-[10px] font-black uppercase tracking-widest ring-1 ${actionTone[log.action] || 'bg-slate-50 text-slate-600 ring-slate-100'}`}>
                                {log.action}
                              </span>
                            </div>

                            <p className="text-base font-black leading-7 text-slate-900">{log.description}</p>

                            <div className="mt-3 flex flex-wrap gap-x-4 gap-y-1 text-xs font-bold text-slate-400">
                              <span>IP: {log.ipAddress || '-'}</span>
                              <span className="max-w-full truncate">Agent: {log.userAgent || '-'}</span>
                            </div>
                          </div>

                          <div className="shrink-0 rounded-2xl bg-slate-50 px-4 py-3 text-right">
                            <p className="text-[10px] font-black uppercase tracking-widest text-slate-400">İşlemi Yapan</p>
                            <p className="mt-1 text-sm font-black text-slate-700">{log.userName || 'Sistem'}</p>
                          </div>
                        </div>
                      </article>
                    ))}
                  </div>
                </div>
              )}

              <div className="mt-6 flex flex-col gap-3 border-t border-slate-100 pt-5 sm:flex-row sm:items-center sm:justify-between">
                <p className="text-xs font-black uppercase tracking-widest text-slate-400">
                  Toplam {auditPagination.total || 0} kayıt | Sayfa {auditPagination.page || auditPage} / {Math.max(auditPagination.pages || 1, 1)}
                </p>
                <div className="flex items-center gap-2">
                  <button
                    type="button"
                    onClick={() => goToAuditPage(auditPage - 1)}
                    disabled={loadingAuditLogs || auditPage <= 1}
                    className="flex items-center gap-2 rounded-xl border border-slate-200 bg-white px-4 py-2 text-xs font-black text-slate-600 transition hover:bg-slate-50 disabled:cursor-not-allowed disabled:opacity-40"
                  >
                    <ChevronLeft size={16} />
                    Geri
                  </button>
                  <button
                    type="button"
                    onClick={() => goToAuditPage(auditPage + 1)}
                    disabled={loadingAuditLogs || auditPage >= Math.max(auditPagination.pages || 1, 1)}
                    className="flex items-center gap-2 rounded-xl border border-slate-200 bg-white px-4 py-2 text-xs font-black text-slate-600 transition hover:bg-slate-50 disabled:cursor-not-allowed disabled:opacity-40"
                  >
                    İleri
                    <ChevronRight size={16} />
                  </button>
                </div>
              </div>
            </section>
          </div>
        )}

        {activeTab === 'locations' && <LocationSettings />}

        {activeTab === 'notifications' && (
          <section className="rounded-3xl border border-slate-200 bg-white p-6 shadow-sm">
            <div className="mb-6 flex flex-col gap-4 lg:flex-row lg:items-end lg:justify-between">
              <div>
                <div className="mb-4 flex h-12 w-12 items-center justify-center rounded-2xl bg-blue-50 text-blue-600">
                  <Bell size={24} />
                </div>
                <h2 className="text-xl font-black text-slate-900">Bildirim Kuralları</h2>
                <p className="mt-2 max-w-2xl text-sm font-medium leading-6 text-slate-500">
                  ERP olayları için e-posta ve uygulama içi bildirim kanallarını merkezi olarak yönetin.
                </p>
              </div>
              <button
                type="button"
                onClick={handleSaveNotificationRules}
                disabled={savingNotificationRules || loadingNotificationRules}
                className="flex items-center justify-center gap-2 rounded-2xl bg-blue-600 px-6 py-3 text-sm font-black text-white shadow-lg shadow-blue-200 transition hover:bg-blue-700 disabled:cursor-not-allowed disabled:opacity-60"
              >
                {savingNotificationRules ? <Loader2 size={18} className="animate-spin" /> : <Save size={18} />}
                Bildirim Ayarlarını Kaydet
              </button>
            </div>

            {loadingNotificationRules ? (
              <div className="space-y-3">
                {[0, 1].map((item) => (
                  <div key={item} className="h-20 animate-pulse rounded-2xl bg-slate-100" />
                ))}
              </div>
            ) : notificationRules.length === 0 ? (
              <div className="flex min-h-64 flex-col items-center justify-center rounded-2xl border border-dashed border-slate-300 bg-slate-50 text-slate-400">
                <Bell size={38} className="opacity-30" />
                <p className="mt-3 text-xs font-black uppercase tracking-widest">Bildirim kuralı bulunamadı</p>
              </div>
            ) : (
              <div className="overflow-hidden rounded-2xl border border-slate-200">
                <table className="w-full text-left">
                  <thead className="bg-slate-50">
                    <tr className="border-b border-slate-200 text-[10px] font-black uppercase tracking-widest text-slate-400">
                      <th className="px-5 py-4">Olay</th>
                      <th className="px-5 py-4 text-center">E-Posta Aktif</th>
                      <th className="px-5 py-4 text-center">Uygulama İçi Aktif</th>
                      <th className="px-5 py-4">Roller</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-100">
                    {notificationRules.map((rule) => (
                      <tr key={rule.eventKey} className="transition-colors hover:bg-slate-50/80">
                        <td className="px-5 py-4">
                          <div className="font-black text-slate-800">{rule.eventName}</div>
                          <div className="mt-1 text-[10px] font-black uppercase tracking-widest text-slate-400">{rule.eventKey}</div>
                        </td>
                        <td className="px-5 py-4 text-center">
                          <input
                            type="checkbox"
                            checked={Boolean(rule.emailActive)}
                            onChange={(event) => updateNotificationRule(rule.eventKey, 'emailActive', event.target.checked)}
                            className="h-5 w-5 rounded border-slate-300 text-blue-600 focus:ring-blue-500"
                          />
                        </td>
                        <td className="px-5 py-4 text-center">
                          <input
                            type="checkbox"
                            checked={Boolean(rule.inAppActive)}
                            onChange={(event) => updateNotificationRule(rule.eventKey, 'inAppActive', event.target.checked)}
                            className="h-5 w-5 rounded border-slate-300 text-blue-600 focus:ring-blue-500"
                          />
                        </td>
                        <td className="px-5 py-4">
                          <div className="flex flex-wrap gap-2">
                            {(rule.rolesToNotify || []).length > 0 ? (
                              rule.rolesToNotify
                                .filter((role) => normalizeRoleName(role) !== 'superadmin')
                                .map((role) => (
                                <span key={role} className="rounded-full bg-slate-100 px-3 py-1 text-[10px] font-black uppercase tracking-widest text-slate-500">
                                  {role}
                                </span>
                              ))
                            ) : (
                              <span className="text-xs font-bold text-slate-400">Rol tanımlı değil</span>
                            )}
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </section>
        )}

        {activePlaceholder && (
          <div className="rounded-2xl border border-dashed border-slate-300 bg-white p-10 text-center shadow-sm">
            <div className="mx-auto mb-4 flex h-16 w-16 items-center justify-center rounded-2xl bg-slate-100 text-slate-400">
              {activePlaceholder.icon}
            </div>
            <h2 className="text-xl font-black text-slate-800">{activePlaceholder.title}</h2>
            <p className="mx-auto mt-2 max-w-xl text-sm font-medium leading-6 text-slate-500">
              {activePlaceholder.description}
            </p>
            <p className="mt-6 text-xs font-black uppercase tracking-widest text-slate-400">
              Bu modül yakında aktif edilecek
            </p>
          </div>
        )}
      </main>
    </div>
  );
};

export default SystemSettings;
